import React, { useState, useEffect, useCallback } from 'react';
import {
  Video, Calendar, Clock, Plus, Play, Copy, Check, RefreshCw, Users, Globe, AlertCircle
} from 'lucide-react';
import { api, ws } from '../../lib/api';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { useMeeting } from '../../context/MeetingContext';
import { CreateMeetingModal } from '../modals/CreateMeetingModal';
import { MeetingDetailsModal } from '../modals/MeetingDetailsModal';
import { EditMeetingModal } from '../modals/EditMeetingModal';
import type { Meeting, MeetingStatusCounts } from '@sahaj/types';

export const MeetingsView: React.FC = () => {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const { joinMeetingRoom } = useMeeting();

  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [statusCounts, setStatusCounts] = useState<MeetingStatusCounts | null>(null);
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'SCHEDULED' | 'ENDED' | 'CANCELLED' | 'EXPIRED'>('ALL');
  const [isLoading, setIsLoading] = useState(false);
  const [isStartingInstant, setIsStartingInstant] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null);

  const fetchMeetings = useCallback(async () => {
    if (!currentWorkspace) return;
    setIsLoading(true);
    setError(null);
    try {
      const [listRes, countsRes] = await Promise.all([
        api.meetings.list(
          currentWorkspace.id,
          statusFilter === 'ALL' ? undefined : statusFilter
        ),
        api.meetings.getStatusCounts(currentWorkspace.id).catch(() => null),
      ]);
      setMeetings(listRes.data || []);
      if (countsRes) setStatusCounts(countsRes);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load meetings');
    } finally {
      setIsLoading(false);
    }
  }, [currentWorkspace, statusFilter]);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  const handleStartInstantMeeting = async () => {
    if (!currentWorkspace || !user || isStartingInstant) return;
    setIsStartingInstant(true);
    setError(null);
    try {
      const newMeeting = await api.meetings.create(currentWorkspace.id, {
        title: `${user.displayName}'s Instant Meeting`,
        description: 'Instant ad-hoc video collaboration',
        meetingType: 'INSTANT',
      });

      fetchMeetings();

      if (ws.getState() !== 'CONNECTED') {
        await ws.connect();
      }

      await joinMeetingRoom(currentWorkspace.id, newMeeting.id);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to start instant meeting');
    } finally {
      setIsStartingInstant(false);
    }
  };


  const handleJoinMeeting = async (meeting: Meeting, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!currentWorkspace) return;
    try {
      if (ws.getState() !== 'CONNECTED') {
        await ws.connect();
      }

      await joinMeetingRoom(currentWorkspace.id, meeting.id);
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'Failed to join meeting room');
    }
  };

  const handleEndMeeting = async (meeting: Meeting, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!currentWorkspace) return;
    if (!confirm(`Are you sure you want to end "${meeting.title}"?`)) return;
    try {
      await api.meetings.end(currentWorkspace.id, meeting.id);
      fetchMeetings();
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'Failed to end meeting');
    }
  };

  const handleCancelMeeting = async (meeting: Meeting, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!currentWorkspace) return;
    if (!confirm(`Are you sure you want to cancel "${meeting.title}"?`)) return;
    try {
      await api.meetings.cancel(currentWorkspace.id, meeting.id);
      fetchMeetings();
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'Failed to cancel meeting');
    }
  };

  const handleCopyLink = (meeting: Meeting, e: React.MouseEvent) => {
    e.stopPropagation();
    const token = meeting.rawJoinToken || meeting.id;
    const linkToCopy = meeting.joinLink || `${window.location.origin}/meet/${token}`;
    navigator.clipboard.writeText(linkToCopy);
    setCopiedTokenId(meeting.id);
    setTimeout(() => setCopiedTokenId(null), 2000);
  };

  const formatDateTime = (isoString?: string, targetTimezone?: string) => {
    if (!isoString) return 'Ad-hoc';
    try {
      const tz = targetTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: tz,
      }).format(new Date(isoString));
    } catch (_e) {
      return new Date(isoString).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-background overflow-hidden">
      {/* Header Bar */}
      <div className="h-14 px-6 border-b border-border flex items-center justify-between bg-card">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <Video className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-base font-bold text-foreground leading-tight">Meetings</h1>
            <p className="text-[11px] text-muted-foreground">
              Schedule & join HD video meetings in {currentWorkspace?.name || 'Workspace'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={fetchMeetings}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition"
            title="Refresh Meetings"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>

          <button
            type="button"
            disabled={isStartingInstant}
            onClick={handleStartInstantMeeting}
            className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition flex items-center gap-1.5 shadow-sm disabled:opacity-50"
          >
            <Clock className="w-4 h-4" /> {isStartingInstant ? 'Starting...' : 'Start Instant'}
          </button>

          <button
            type="button"
            onClick={() => setIsCreateModalOpen(true)}
            className="px-3.5 py-1.5 text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg transition flex items-center gap-1.5 shadow-sm"
          >
            <Plus className="w-4 h-4" /> Schedule Meeting
          </button>
        </div>
      </div>

      {/* Main Body */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Status Filter Tabs */}
        <div className="flex items-center gap-2 border-b border-border pb-3">
          {(['ALL', 'ACTIVE', 'SCHEDULED', 'ENDED', 'CANCELLED', 'EXPIRED'] as const).map((filter) => {
            let count: number | undefined = undefined;
            if (statusCounts) {
              if (filter === 'ALL') count = statusCounts.allCount;
              else if (filter === 'ACTIVE') count = statusCounts.activeCount;
              else if (filter === 'SCHEDULED') count = statusCounts.scheduledCount;
              else if (filter === 'ENDED') count = statusCounts.endedCount;
              else if (filter === 'CANCELLED') count = statusCounts.cancelledCount;
              else if (filter === 'EXPIRED') count = statusCounts.expiredCount;
            }
            return (
              <button
                key={filter}
                type="button"
                onClick={() => setStatusFilter(filter)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition flex items-center gap-1.5 ${
                  statusFilter === filter
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <span>{filter}</span>
                {count !== undefined && (
                  <span className={`px-1.5 py-0.2 text-[10px] rounded-full font-bold ${
                    statusFilter === filter ? 'bg-white/20 text-white' : 'bg-muted/80 text-muted-foreground'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Distinct Error Banner */}
        {error && (
          <div className="p-4 bg-destructive/15 text-destructive rounded-xl border border-destructive/20 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2 text-xs font-medium">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
            <button
              type="button"
              onClick={fetchMeetings}
              className="px-3 py-1 text-xs bg-destructive text-white rounded-md font-semibold hover:bg-destructive/90 transition"
            >
              Retry
            </button>
          </div>
        )}

        {/* Distinct Loading / Empty / Data States */}
        {isLoading && meetings.length === 0 ? (
          <div className="py-16 text-center text-xs text-muted-foreground flex flex-col items-center justify-center gap-2">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span>Loading meetings...</span>
          </div>
        ) : !error && meetings.length === 0 ? (
          <div className="py-16 text-center space-y-3 bg-muted/10 border border-dashed border-border rounded-xl p-8">
            <div className="p-3 bg-muted/30 rounded-full w-fit mx-auto text-muted-foreground">
              <Calendar className="w-8 h-8" />
            </div>
            <h3 className="text-sm font-bold text-foreground">No meetings found</h3>
            <p className="text-xs text-muted-foreground max-w-sm mx-auto">
              {statusFilter === 'ALL'
                ? 'Start an instant meeting or schedule your first team sync.'
                : `No meetings matching status "${statusFilter}".`}
            </p>
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
              className="px-4 py-2 text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg transition shadow-sm"
            >
              Schedule Meeting
            </button>
          </div>
        ) : meetings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {meetings.map((m) => {
              const isHost = m.host.id === user?.id;
              return (
                <div
                  key={m.id}
                  onClick={() => {
                    setSelectedMeeting(m);
                    setIsDetailsModalOpen(true);
                  }}
                  className="p-4 bg-card border border-border hover:border-primary/50 rounded-xl shadow-sm hover:shadow-md transition cursor-pointer flex flex-col justify-between space-y-4 group"
                >
                  {/* Top Bar */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${
                        m.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                        m.status === 'SCHEDULED' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                        m.status === 'CANCELLED' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                        m.status === 'EXPIRED' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {m.status}
                      </span>
                      <span className="text-[10px] text-muted-foreground uppercase font-bold">
                        {m.meetingType}
                      </span>
                    </div>

                    <h3 className="text-sm font-bold text-foreground group-hover:text-primary transition line-clamp-1">
                      {m.title}
                    </h3>
                    {m.description && (
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {m.description}
                      </p>
                    )}
                  </div>

                  {/* Date & Host Info */}
                  <div className="space-y-1.5 text-xs text-muted-foreground pt-2 border-t border-border/50">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-primary shrink-0" />
                      <span className="truncate">{formatDateTime(m.startTime, m.timezone)}</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" /> {m.activeParticipantCount || 0} Active / {m.participants?.length || 0} Invited
                      </span>
                      <span className="flex items-center gap-1">
                        <Globe className="w-3 h-3" /> {m.timezone}
                      </span>
                    </div>
                  </div>

                  {/* Actions depending strictly on status & role */}
                  <div className="flex items-center justify-between pt-2 border-t border-border/50">
                    <button
                      type="button"
                      onClick={(e) => handleCopyLink(m, e)}
                      className="p-1.5 text-xs bg-muted hover:bg-muted/80 text-foreground rounded-lg transition flex items-center gap-1"
                      title="Copy meeting link"
                    >
                      {copiedTokenId === m.id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      <span className="text-[11px] font-medium">{copiedTokenId === m.id ? 'Copied' : 'Link'}</span>
                    </button>

                    {m.status === 'ACTIVE' ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={(e) => handleJoinMeeting(m, e)}
                          className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition flex items-center gap-1 shadow-sm"
                        >
                          <Play className="w-3 h-3 fill-current" /> Join
                        </button>
                        {isHost && (
                          <button
                            type="button"
                            onClick={(e) => handleEndMeeting(m, e)}
                            className="px-2.5 py-1.5 text-xs font-semibold bg-destructive hover:bg-destructive/90 text-white rounded-lg transition shadow-sm"
                          >
                            End
                          </button>
                        )}
                      </div>
                    ) : m.status === 'SCHEDULED' ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[11px] text-muted-foreground italic flex items-center gap-1 font-medium bg-muted/30 px-2 py-1 rounded">
                          <Clock className="w-3 h-3" /> Waiting for scheduled start
                        </span>
                        {isHost && (
                          <button
                            type="button"
                            onClick={(e) => handleCancelMeeting(m, e)}
                            className="px-2.5 py-1.5 text-xs font-semibold bg-destructive/10 hover:bg-destructive/20 text-destructive rounded-lg transition shadow-sm"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-[11px] text-muted-foreground italic font-medium">
                        {m.status === 'ENDED' ? 'Meeting ended' : m.status === 'CANCELLED' ? 'Meeting cancelled' : 'Meeting expired'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Modals */}
      {currentWorkspace && (
        <>
          <CreateMeetingModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            workspaceId={currentWorkspace.id}
            onCreated={fetchMeetings}
          />

          <MeetingDetailsModal
            isOpen={isDetailsModalOpen}
            onClose={() => setIsDetailsModalOpen(false)}
            workspaceId={currentWorkspace.id}
            meeting={selectedMeeting}
            currentUserId={user?.id || ''}
            onRefresh={() => {
              fetchMeetings();
              if (selectedMeeting) {
                api.meetings.getDetails(currentWorkspace.id, selectedMeeting.id)
                  .then((updated) => setSelectedMeeting(updated))
                  .catch(() => {});
              }
            }}
            onJoinMeeting={handleJoinMeeting}
            onOpenEdit={(m) => {
              setIsDetailsModalOpen(false);
              setEditingMeeting(m);
              setIsEditModalOpen(true);
            }}
          />

          <EditMeetingModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            workspaceId={currentWorkspace.id}
            meeting={editingMeeting}
            onUpdated={fetchMeetings}
          />
        </>
      )}
    </div>
  );
};
